import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Mail, User } from 'lucide-react';
import { useAppDispatch } from '@/app/hooks';
import { credentialsReceived } from '@/features/auth/authSlice';
import { organizationSelected } from '@/features/tenant/tenantSlice';
import { useRegisterMutation } from '@/api/endpoints';
import { apiErrorMessage, apiFieldErrors } from '@/api/baseApi';
import { Alert, Button, Input } from '@/components/ui';
import { AuthLayout } from './AuthLayout';

/** Mirrors the server's rules so the user sees them before submitting. */
function passwordChecklist(password: string): { label: string; met: boolean }[] {
  return [
    { label: 'At least 10 characters', met: password.length >= 10 },
    { label: 'An uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'A lowercase letter', met: /[a-z]/.test(password) },
    { label: 'A number', met: /[0-9]/.test(password) },
  ];
}

export function RegisterPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation') ?? undefined;

  const [register, { isLoading, error }] = useRegisterMutation();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });

  const fieldErrors = apiFieldErrors(error);
  const checklist = passwordChecklist(form.password);
  const passwordReady = checklist.every((rule) => rule.met);

  const update = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  const onSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();

    const result = await register({ ...form, invitationToken }).unwrap().catch(() => null);
    if (!result) return;

    dispatch(
      credentialsReceived({
        user: result.user,
        accessToken: result.tokens.accessToken,
        refreshToken: result.tokens.refreshToken,
        organizations: result.organizations,
      }),
    );

    const first = result.organizations[0];
    if (first) {
      dispatch(organizationSelected(first.id));
      navigate('/app/dashboard', { replace: true });
      return;
    }
    navigate('/onboarding', { replace: true });
  };

  return (
    <AuthLayout
      title={invitationToken ? 'Accept your invitation' : 'Create your account'}
      subtitle={
        invitationToken
          ? 'Set a password to join the business you were invited to.'
          : 'Set up your business in a few minutes.'
      }
      footer={
        <>
          Already have an account?{' '}
          <Link to="/auth/login" className="font-medium text-brand-600 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        {error && <Alert tone="danger">{apiErrorMessage(error, 'Registration failed')}</Alert>}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="First name"
            required
            icon={User}
            autoComplete="given-name"
            value={form.firstName}
            onChange={update('firstName')}
            error={fieldErrors.firstName}
          />
          <Input
            label="Last name"
            required
            autoComplete="family-name"
            value={form.lastName}
            onChange={update('lastName')}
            error={fieldErrors.lastName}
          />
        </div>

        <Input
          label="Work email"
          type="email"
          required
          icon={Mail}
          autoComplete="email"
          value={form.email}
          onChange={update('email')}
          error={fieldErrors.email}
        />

        <div>
          <Input
            label="Password"
            type="password"
            required
            icon={Lock}
            autoComplete="new-password"
            value={form.password}
            onChange={update('password')}
            error={fieldErrors.password}
          />
          <ul className="mt-2 grid gap-1 sm:grid-cols-2">
            {checklist.map((rule) => (
              <li
                key={rule.label}
                className={rule.met ? 'text-xs text-emerald-600 dark:text-emerald-400' : 'text-xs text-muted'}
              >
                {rule.met ? '✓' : '○'} {rule.label}
              </li>
            ))}
          </ul>
        </div>

        <Button type="submit" size="lg" fullWidth loading={isLoading} disabled={!passwordReady}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
